import { useGummiwormContext } from "@/context/GummiwormContext";
import {
  FC,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useReducer,
  useRef,
  useState,
} from "react";
import { assetIdKey, formatAssetLabel, getAssetDecimals } from "@/lib/marketUtils";
import { Market, OrderbookDepth } from "@/lib/wsTypes";
import {
  DEFAULT_LEVEL_CAP,
  DELTA_TTL_MS,
  DisplayLevel,
  LevelChange,
  RawLevel,
  aggregateLevels,
  defaultTick,
  diffLevels,
  formatCompactSize,
  formatTick,
  liveChanges,
  sizesByPrice,
  tickDecimals,
  tickLadder,
} from "@/lib/orderbookLevels";
import { TradingPaneState } from "./TradingPaneState";

type ViewMode = "both" | "bids" | "asks";

const FRAC_BITS = 60n;

/** Convert fixed-point u128 price to a display number. */
function priceToNumber(raw: bigint): number {
  const integer = Number(raw >> FRAC_BITS);
  const fractional = Number(raw & ((1n << FRAC_BITS) - 1n)) / Number(1n << FRAC_BITS);
  return integer + fractional;
}

/** One side's levels straight off the book, unclustered and unsorted.
 * `aggregateLevels` does the clustering, ordering and capping. */
function rawLevels(depths: readonly OrderbookDepth[], side: "Bid" | "Ask"): RawLevel[] {
  const out: RawLevel[] = [];
  for (const d of depths) {
    const amount = side === "Bid" ? d.bidAmount : d.askAmount;
    if (amount === 0n) continue;
    out.push({ price: priceToNumber(d.price), size: Number(amount) });
  }
  return out;
}

/** Best price on a side, straight from the raw levels.
 *
 * A plain loop rather than `Math.min(...prices)`: a market maker can rest a
 * thousand orders, and spreading that many arguments risks the call-stack
 * limit for no benefit.
 */
function bestPrice(levels: readonly RawLevel[], side: "bid" | "ask"): number | undefined {
  let best: number | undefined;
  for (const level of levels) {
    if (best === undefined) best = level.price;
    else if (side === "bid" ? level.price > best : level.price < best) best = level.price;
  }
  return best;
}

/**
 * Live size changes per price for one side, expiring after `DELTA_TTL_MS`.
 *
 * The book is diffed against its own previous state, so `resetKey` must name
 * everything that renumbers the prices -- the market and the tick. Without the
 * reset, changing either would register every row as a fresh change and light
 * the whole book up for liquidity that merely moved rows.
 *
 * The diff runs against `levels`' identity rather than on every render: it
 * comes from a `useMemo`, so a repeated render with the same reference is not
 * a new update, and re-diffing it would compare a book against itself.
 */
function useLevelChanges(levels: DisplayLevel[], resetKey: string): Map<number, number> {
  const previous = useRef<Map<number, number>>(new Map());
  const changes = useRef<Map<number, LevelChange>>(new Map());
  const seenKey = useRef(resetKey);
  const seenLevels = useRef<DisplayLevel[] | null>(null);
  const [, rerender] = useReducer((n: number) => n + 1, 0);

  if (seenKey.current !== resetKey) {
    seenKey.current = resetKey;
    seenLevels.current = null;
    previous.current = new Map();
    changes.current = new Map();
  }

  const now = Date.now();
  if (seenLevels.current !== levels) {
    seenLevels.current = levels;
    changes.current = diffLevels(previous.current, levels, now, changes.current);
    previous.current = sizesByPrice(levels);
  }

  const live = liveChanges(changes.current, now);

  // A quiet book renders nothing, so without a timer the last change would sit
  // there indefinitely -- undated, and read as though it had just happened.
  // The timer stops once everything has aged out.
  useEffect(() => {
    if (live.size === 0) return;
    const timer = setTimeout(rerender, Math.min(500, DELTA_TTL_MS));
    return () => clearTimeout(timer);
  });

  return live;
}

interface OrderBookProps {
  selectedMarket: Market | null;
  onSelectPrice?: (price: number) => void;
}

export const OrderBook: FC<OrderBookProps> = ({ selectedMarket, onSelectPrice }) => {
  const { ledgerView } = useGummiwormContext();
  const [viewMode, setViewMode] = useState<ViewMode>("both");
  const [autoCenterSpread, setAutoCenterSpread] = useState(true);
  const scrollContainerRef = useRef<HTMLDivElement | null>(null);
  const spreadRowRef = useRef<HTMLDivElement | null>(null);
  const centeredSignatureRef = useRef<string>("");
  const isProgrammaticScrollRef = useRef(false);

  // Query by the selected market
  const pairKey = useMemo(() => {
    if (!selectedMarket) return '';
    const baseKey = assetIdKey(selectedMarket.tradingPair.base);
    const quoteKey = assetIdKey(selectedMarket.tradingPair.quote);
    return `${baseKey}/${quoteKey}`;
  }, [selectedMarket]);
  const depths = useMemo(() => {
    if (!ledgerView) return [];
    if (!ledgerView.depths[pairKey]) return [];
    return ledgerView.depths[pairKey];
  }, [ledgerView?.depths, pairKey]);

  const rawAsks = useMemo(() => rawLevels(depths, "Ask"), [depths]);
  const rawBids = useMemo(() => rawLevels(depths, "Bid"), [depths]);

  // The spread comes from the RAW best prices, never the clustered ones. A
  // clustered price is rounded away from the spread by up to a tick, so quoting
  // it here would overstate the spread by as much as two ticks.
  const bestAsk = bestPrice(rawAsks, "ask");
  const bestBid = bestPrice(rawBids, "bid");

  // Clustering granularity. `null` means "follow the market", which is what a
  // fresh market starts on; picking a tick pins it until the market changes.
  const [tick, setTick] = useState<number | null>(null);
  useEffect(() => setTick(null), [pairKey]);
  const referencePrice = bestAsk ?? bestBid ?? 0;
  const ladder = useMemo(() => tickLadder(referencePrice), [referencePrice]);
  const activeTick = tick ?? defaultTick(referencePrice);

  // Both sides take the SAME cap, which is what keeps the two columns equal in
  // length and the spread row in the middle. Before this, one side could run to
  // hundreds of rows while the other was clipped.
  const askLevels = useMemo(
    () => aggregateLevels(rawAsks, "ask", activeTick, DEFAULT_LEVEL_CAP),
    [rawAsks, activeTick],
  );
  const bidLevels = useMemo(
    () => aggregateLevels(rawBids, "bid", activeTick, DEFAULT_LEVEL_CAP),
    [rawBids, activeTick],
  );

  // What each side's resting size did since the last update. Reset whenever the
  // market or the tick changes, since either renumbers the prices being diffed.
  const changeKey = `${pairKey}:${activeTick}`;
  const askChanges = useLevelChanges(askLevels, changeKey);
  const bidChanges = useLevelChanges(bidLevels, changeKey);

  const quoteLabel = selectedMarket ? formatAssetLabel(selectedMarket.tradingPair.quote) : "Quote";
  const quoteDecimals = selectedMarket ? getAssetDecimals(selectedMarket.tradingPair.quote) : 0;
  const priceDecimals = tickDecimals(activeTick);

  const showAsks = viewMode !== "bids";
  const showBids = viewMode !== "asks";
  const showSpread = viewMode === "both";

  const spread = bestAsk != null && bestBid != null ? bestAsk - bestBid : null;
  const spreadPct = spread != null && bestAsk != null && bestAsk > 0
    ? ((spread / bestAsk) * 100).toFixed(2)
    : null;

  const getSpreadCenteredScrollTop = useCallback(() => {
    const container = scrollContainerRef.current;
    const spreadRow = spreadRowRef.current;
    if (!container || !spreadRow) return null;

    const containerRect = container.getBoundingClientRect();
    const spreadRect = spreadRow.getBoundingClientRect();
    const spreadTopWithinContainer =
      container.scrollTop + (spreadRect.top - containerRect.top);
    const targetTop =
      spreadTopWithinContainer - (container.clientHeight - spreadRect.height) / 2;
    const maxTop = Math.max(0, container.scrollHeight - container.clientHeight);
    return Math.max(0, Math.min(targetTop, maxTop));
  }, []);

  const setProgrammaticScrollTop = useCallback((top: number) => {
    const container = scrollContainerRef.current;
    if (!container) return;

    isProgrammaticScrollRef.current = true;
    container.scrollTop = top;

    requestAnimationFrame(() => {
      isProgrammaticScrollRef.current = false;
    });
  }, []);

  const resetScrollPosition = useCallback(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    if (showSpread) {
      setAutoCenterSpread(true);
      const targetTop = getSpreadCenteredScrollTop();
      if (targetTop == null) return;
      setProgrammaticScrollTop(targetTop);
      return;
    }

    setAutoCenterSpread(false);
    setProgrammaticScrollTop(viewMode === "asks" ? container.scrollHeight : 0);
  }, [getSpreadCenteredScrollTop, setProgrammaticScrollTop, showSpread, viewMode]);

  useLayoutEffect(() => {
    const signature = `${pairKey}:${viewMode}`;
    if (centeredSignatureRef.current === signature) return;
    centeredSignatureRef.current = signature;
    setAutoCenterSpread(showSpread);
    resetScrollPosition();
  }, [pairKey, viewMode, resetScrollPosition, showSpread]);

  useLayoutEffect(() => {
    if (!showSpread || !autoCenterSpread) return;
    const targetTop = getSpreadCenteredScrollTop();
    if (targetTop == null) return;
    setProgrammaticScrollTop(targetTop);
  }, [
    askLevels.length,
    bidLevels.length,
    bestAsk,
    bestBid,
    autoCenterSpread,
    getSpreadCenteredScrollTop,
    setProgrammaticScrollTop,
    showSpread,
  ]);

  const handleScroll = useCallback(() => {
    if (!showSpread || isProgrammaticScrollRef.current) return;
    const container = scrollContainerRef.current;
    const spreadRow = spreadRowRef.current;
    if (!container || !spreadRow) return;

    const containerRect = container.getBoundingClientRect();
    const spreadRect = spreadRow.getBoundingClientRect();
    const spreadMidpoint = spreadRect.top - containerRect.top + spreadRect.height / 2;
    const containerMidpoint = container.clientHeight / 2;

    if (Math.abs(spreadMidpoint - containerMidpoint) > 12) {
      setAutoCenterSpread(false);
    }
  }, [showSpread]);

  // Depth bars share ONE scale across both sides, so a 1000-deep side reads as
  // ten times a 100-deep side rather than both filling their row. The max is the
  // larger of the two sides' deepest cumulative total (and per-level delta).
  //
  // Scaled against the VISIBLE rows, not the whole book: with a cap in place the
  // deepest row of a thousand-level side would otherwise flatten every bar on
  // screen to a sliver.
  const maxAskTotal = askLevels.length > 0 ? askLevels[askLevels.length - 1].total : 1;
  const maxBidTotal = bidLevels.length > 0 ? bidLevels[bidLevels.length - 1].total : 1;
  const maxTotal = Math.max(maxAskTotal, maxBidTotal, 1);

  // For "both" mode, show asks reversed so best ask is at the bottom (near spread)
  const displayAsks = showSpread ? [...askLevels].reverse() : askLevels;

  if (!selectedMarket) {
    return (
      <div className="sr-panel flex h-full min-h-[20rem] flex-col">
        <TradingPaneState
          eyebrow="Depth"
          title="No market selected"
          message="Pick a trading pair to inspect the order book."
        />
      </div>
    );
  }

  if (askLevels.length === 0 && bidLevels.length === 0) {
    return (
      <div className="sr-panel flex h-full min-h-[20rem] flex-col">
        <div className="sr-panel-header">
          <h3 className="text-[11px] font-medium uppercase tracking-wider text-sr-neutral-300">
            Order Book
          </h3>
        </div>
        <TradingPaneState
          eyebrow="Depth"
          title="No liquidity yet"
          message="This market has no active bids or asks. Check back after the first orders land."
        />
      </div>
    );
  }

  return (
    <section className="sr-panel flex h-full flex-col" aria-label="Order book">
      {/* Header */}
      <div className="sr-panel-header border-none pb-2">
        <h3 className="text-[11px] font-medium text-sr-neutral-300 uppercase tracking-wider">
          Order Book
        </h3>
        <div className="flex items-center gap-2">
          <label className="sr-only" htmlFor="orderbook-tick">
            Price grouping
          </label>
          <select
            id="orderbook-tick"
            value={activeTick}
            onChange={(e) => setTick(Number(e.target.value))}
            title="Group nearby prices into one row"
            className="sr-focus-ring rounded-md border border-sr-border-subtle bg-transparent px-1.5 py-1 text-[10px] tabular-nums text-sr-neutral-300 transition-colors hover:bg-white/5 hover:text-white"
          >
            {ladder.map((rung) => (
              <option key={rung} value={rung} className="bg-sr-neutral-900 text-white">
                {formatTick(rung)}
              </option>
            ))}
          </select>
          {showSpread && !autoCenterSpread && (
            <button
              type="button"
              onClick={resetScrollPosition}
              className="sr-focus-ring rounded-md border border-sr-border-subtle px-2 py-1 text-[10px] font-medium uppercase tracking-wider text-sr-neutral-300 transition-colors hover:bg-white/5 hover:text-white"
            >
              Reset
            </button>
          )}
          <div className="flex items-center gap-0.5" role="toolbar" aria-label="Order book view mode">
            <button
              onClick={() => setViewMode("both")}
              className="sr-focus-ring rounded p-1"
              title="Both"
              aria-pressed={viewMode === "both"}
              aria-label="Show asks and bids"
            >
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                <rect x="2" y="2" width="5" height="5" rx="1" fill={viewMode === "both" ? "var(--color-sr-salmon)" : "var(--color-sr-neutral-700)"} />
                <rect x="9" y="2" width="5" height="5" rx="1" fill={viewMode === "both" ? "var(--color-sr-salmon)" : "var(--color-sr-neutral-700)"} />
                <rect x="2" y="9" width="5" height="5" rx="1" fill={viewMode === "both" ? "var(--color-sr-mint)" : "var(--color-sr-neutral-700)"} />
                <rect x="9" y="9" width="5" height="5" rx="1" fill={viewMode === "both" ? "var(--color-sr-mint)" : "var(--color-sr-neutral-700)"} />
              </svg>
            </button>
            <button
              onClick={() => setViewMode("bids")}
              className="sr-focus-ring rounded p-1"
              title="Bids"
              aria-pressed={viewMode === "bids"}
              aria-label="Show bids only"
            >
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                <rect x="2" y="2" width="5" height="5" rx="1" fill={viewMode === "bids" ? "var(--color-sr-mint)" : "var(--color-sr-neutral-700)"} />
                <rect x="9" y="2" width="5" height="5" rx="1" fill={viewMode === "bids" ? "var(--color-sr-mint)" : "var(--color-sr-neutral-700)"} />
                <rect x="2" y="9" width="5" height="5" rx="1" fill={viewMode === "bids" ? "var(--color-sr-mint)" : "var(--color-sr-neutral-700)"} />
                <rect x="9" y="9" width="5" height="5" rx="1" fill={viewMode === "bids" ? "var(--color-sr-mint)" : "var(--color-sr-neutral-700)"} />
              </svg>
            </button>
            <button
              onClick={() => setViewMode("asks")}
              className="sr-focus-ring rounded p-1"
              title="Asks"
              aria-pressed={viewMode === "asks"}
              aria-label="Show asks only"
            >
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                <rect x="2" y="2" width="5" height="5" rx="1" fill={viewMode === "asks" ? "var(--color-sr-salmon)" : "var(--color-sr-neutral-700)"} />
                <rect x="9" y="2" width="5" height="5" rx="1" fill={viewMode === "asks" ? "var(--color-sr-salmon)" : "var(--color-sr-neutral-700)"} />
                <rect x="2" y="9" width="5" height="5" rx="1" fill={viewMode === "asks" ? "var(--color-sr-salmon)" : "var(--color-sr-neutral-700)"} />
                <rect x="9" y="9" width="5" height="5" rx="1" fill={viewMode === "asks" ? "var(--color-sr-salmon)" : "var(--color-sr-neutral-700)"} />
              </svg>
            </button>
          </div>
        </div>
      </div>

      {/* Column headers */}
      <div className="grid grid-cols-[1.3fr_0.8fr_0.8fr_0.8fr] items-end gap-1 text-[9px] text-sr-neutral-500 uppercase tracking-wider px-2 pb-1">
        <span className="px-1">Price</span>
        <span className="text-right">Delta</span>
        <span className="text-right">Size</span>
        <span className="text-right px-2">Sum</span>
      </div>

      {/* The scroll port holds ONE flex column, and the two sides size to their
          own content inside it.

          Neither side may be `flex-1`: that is `flex: 1 1 0%`, so with
          `min-h-0` a side shrinks towards zero height while its rows keep their
          own, and the overflow paints straight over the other side. The old
          `justify-end` masked that by clipping the overflow at the top edge --
          which is the same thing that made the far asks unreachable, since
          `scrollTop` cannot go below zero.

          `min-h-full` on the column is what still pushes a SHORT ask list down
          against the spread: the column is at least as tall as the port, so
          `mt-auto` below has room to work. When the rows exceed the port the
          column simply grows and the port scrolls. */}
      <div
        ref={scrollContainerRef}
        className="min-h-0 flex-1 overflow-y-auto pb-5"
        onScroll={handleScroll}
        style={{ overflowAnchor: "none" }}
      >
        <div className="flex min-h-full flex-col">
        {/* Asks */}
        {showAsks && (
          <div className={showSpread ? "mt-auto flex shrink-0 flex-col" : "flex shrink-0 flex-col"}>
            {displayAsks.length > 0 ? displayAsks.map((level, i) => (
              <Row key={`ask-${level.price}-${i}`} level={level} side="ask" maxTotal={maxTotal} delta={askChanges.get(level.price)} quoteDecimals={quoteDecimals} priceDecimals={priceDecimals} onSelect={onSelectPrice} />
            )) : (
              <div className="flex items-end justify-center pb-2">
                <span className="text-xs text-sr-neutral-700">No asks</span>
              </div>
            )}
          </div>
        )}

        {/* Spread */}
        {showSpread && (
          <div
            ref={spreadRowRef}
            className="flex h-7 shrink-0 items-center justify-between border-y border-sr-border-subtle/50 px-3 text-[10px]"
          >
            <span className="text-sr-neutral-500">Spread</span>
            {spread != null ? (
              <div className="flex items-center gap-2">
                <span className="text-sr-neutral-300 tabular-nums">{spreadPct}%</span>
                <span className="text-white tabular-nums font-display">{spread.toFixed(6)} {quoteLabel}</span>
              </div>
            ) : (
              <span className="text-sr-neutral-700">—</span>
            )}
          </div>
        )}

        {/* Bids. `mb-auto` mirrors the asks' `mt-auto`: a short bid list stays
            up against the spread instead of drifting to the bottom. */}
        {showBids && (
          <div className={showSpread ? "mb-auto flex shrink-0 flex-col" : "flex shrink-0 flex-col"}>
            {bidLevels.length > 0 ? bidLevels.map((level, i) => (
              <Row key={`bid-${level.price}-${i}`} level={level} side="bid" maxTotal={maxTotal} delta={bidChanges.get(level.price)} quoteDecimals={quoteDecimals} priceDecimals={priceDecimals} onSelect={onSelectPrice} />
            )) : (
              <div className="flex items-start justify-center pt-2">
                <span className="text-xs text-sr-neutral-700">No bids</span>
              </div>
            )}
          </div>
        )}
        </div>
      </div>
    </section>
  );
};

const Row: FC<{
  level: DisplayLevel;
  side: "ask" | "bid";
  maxTotal: number;
  /** Size change at this price since the last update, while it is still fresh.
   * Undefined when nothing has moved here recently. */
  delta?: number;
  quoteDecimals: number;
  priceDecimals: number;
  onSelect?: (price: number) => void;
}> = ({ level, side, maxTotal, delta, quoteDecimals, priceDecimals, onSelect }) => {
  const totalPct = maxTotal > 0 ? (level.total / maxTotal) * 100 : 0;
  const isAsk = side === "ask";
  // Change is a number in its own column, and nothing else. It carries no bar.
  //
  // A bar was the real problem: drawn at the same width and alignment as the
  // depth bar, a large change repainted the whole row, so mint and salmon --
  // which mean "bid" and "ask" everywhere else -- appeared to spill across the
  // book. A small right-aligned figure does not read that way, so direction can
  // go back to the two colours everyone already knows here.
  //
  // Magnitude comes from the digits: the column is tabular, so a bigger number
  // is a wider number, and no bar is needed to say so.
  const added = (delta ?? 0) > 0;
  // Sizes arrive in the quote asset's base units. Scale once here, then write
  // compactly: clustering sums sizes, so full precision no longer fits the
  // column and used to spill into the neighbouring one.
  const scale = 10 ** quoteDecimals;
  const formatQuote = (value: number) => formatCompactSize(value / scale);

  return (
    <div
      className="relative grid h-7 shrink-0 grid-cols-[1.3fr_0.8fr_0.8fr_0.8fr] items-center gap-1 px-2 text-[10px] hover:bg-white/5 cursor-pointer"
      onClick={() => onSelect?.(level.price)}
      title={level.merged > 1 ? `${level.merged} price levels grouped` : undefined}
    >
      <div
        className={`absolute inset-y-0 right-0 pointer-events-none ${isAsk ? "bg-sr-salmon/20" : "bg-sr-mint/15"}`}
        style={{ width: `${totalPct}%` }}
      />
      <span className={`relative truncate tabular-nums font-display px-1 ${isAsk ? "text-sr-salmon" : "text-sr-mint"}`}>
        {level.price.toFixed(priceDecimals)}
      </span>
      {/* Blank when nothing has moved here lately. A column of zeroes would
          bury the handful of rows that actually changed. */}
      <span
        className={`relative truncate text-right tabular-nums ${added ? "text-sr-mint" : "text-sr-salmon"}`}
      >
        {delta != null ? `${added ? "+" : "-"}${formatQuote(Math.abs(delta))}` : ""}
      </span>
      <span className="relative truncate text-right text-white tabular-nums">
        {formatQuote(level.size)}
      </span>
      <span className="relative truncate text-right text-white tabular-nums px-1">
        {formatQuote(level.total)}
      </span>
    </div>
  );
};
